import * as React from 'react';
import moment from 'moment';

import { ICalendarState, IEventFilter } from '../classes/calendar';
import { connect } from 'react-redux';
import { ICalendarAppState } from '../redux-state';
import { ICalendarAppText } from '../redux-state/interfaces/text/calendar-app.text';
import { CalendarDeviceMode, CalendarViewMode } from '../redux-state/interfaces/calendar-settings';
import { setToGrid, setToList } from '../redux-state/actions/calendar-settings.actions';
import CalendarEventToggleView from './calendar-event-toggle.view';
import { CalendarApi } from '../common/calendar-api';
import { IEvent, IOption } from '../interfaces';
import { removeEventFilter, setCurrentMonth, setEventType, upsertEventFilter } from '../redux-state/actions/calendar.actions';
import { generateEventFilter } from '../helpers/filter-by-query';
import { getMonthElement, removeLoader, scrollToMonth } from '../helpers/scroll-to-month';
import { uniq } from 'lodash';
import { IMonth } from '../interfaces/month';

interface ICalendarEventTogglePropsExternalProps {

}

interface StateToProps {
    text: ICalendarAppText;
    selectedEventType: string;
    startDate: moment.Moment;
    endDate: moment.Moment;
    /**
     * The furthermost month that has been loaded onto the calendar.
     */
    lastLoadedMonth: moment.Moment;
    /**
     * The last month that can be loaded onto the calendar.
     */
    lastMonthToLoad: moment.Moment;
    currentFilters: Map<string, string>;
    eventMonths: IMonth<IEvent>[];
    isUpdating: boolean;
}

interface DispatchToProps {
    selectFilterType: (eventType: string) => void;
    getFirstOccurenceDateForFilters: (filters: any, lastMonthToLoad: moment.Moment, lastLoadedMonth: moment.Moment) => void;

}

interface EventFilterState {
    fromApi: boolean;
    waitingForApi: boolean;
    options: IOption[];
}

export type CalendarEventToggleProps = ICalendarEventTogglePropsExternalProps  & StateToProps & DispatchToProps;
export type CalendarEventToggleViewProps = CalendarEventToggleProps & EventFilterState;
export class CalendarEventToggleBase extends React.Component<CalendarEventToggleProps, EventFilterState> {
    constructor(props: CalendarEventToggleProps) {
        super(props);

        this.state = {
            fromApi: false,
            waitingForApi: true,
            options: []
        };
    }

    componentDidMount() {
        CalendarApi.getFiltersFor("eventType", this.props.startDate, this.props.endDate).then(response => {
            this.setState({
                fromApi: true,
                waitingForApi: false,
                options: response
            });
        }).catch(err => {
            console.error(err);
        });
    }

    componentDidUpdate(prevProps: CalendarEventToggleProps, prevState: EventFilterState) {
        if ((prevProps.currentFilters.has("eventType") || this.props.currentFilters.has("eventType")) && prevProps.currentFilters.get("eventType") !== this.props.currentFilters.get("eventType") && this.props) {
            setTimeout(() => removeLoader(), 1000);
        }
        if (!this.state.fromApi && !this.state.waitingForApi && !prevProps.lastLoadedMonth.isSame(this.props.lastLoadedMonth)) {
            this.setState({
                options: this.calculateOptions(this.props)
            });
        }
    }


    private calculateOptions(props: CalendarEventToggleProps): IOption[] {
        // flatten events twice because month -> day -> events
        var propertyToFilter = "eventType";
        const events = ([] as IEvent[]).concat(...props.eventMonths.map(month => ([] as IEvent[]).concat(...month.events.days.map(day => day.events))));

        if (events.length > 0) {
            if (Array.isArray(events[0]["eventType"])) {
                // These are all casted as any because it's not specifically known what kind of objects this is expected to support.
                const allOptions = uniq(([] as any[]).concat(...events.map(event => (event[propertyToFilter] as any[]))));

                return allOptions.map(option => {
                    return {
                        text: option.toString(),
                        value: option.toString(),
                        firstOccurance: ""
                    }
                });
            }
            else {
                const mappedProperty = uniq(events.map(event => event[propertyToFilter]));

                return mappedProperty.map(eventProperty => {
                    return {
                        text: eventProperty.toString(),
                        value: eventProperty.toString(),
                        firstOccurance: ""
                    }
                });
            }
        }

        // If empty
        return [];
    }

    render() {
        return (
            <>
                <CalendarEventToggleView {...this.props} {...this.state} />
            </>
        )
    }
}

function mapStateToProps(state: ICalendarAppState, ownProps: ICalendarEventTogglePropsExternalProps): StateToProps {
    var updatedEventList = state.calendarText.eventFilterDefault.split(",").concat(state.calendar.availableEventTypes);
    const startDate = moment(state.calendar.loadedMonths[0]).startOf('day');
    const endDate = moment(state.calendarSettings.monthsOnCalendar[state.calendarSettings.monthsOnCalendar.length - 1]).endOf('month');


    var selectedEventType = "";
    if (state.calendar.eventFilter && state.calendar.eventFilter.length > 0 && state.calendar.eventFilter.filter(ef => ef.key == "eventType").length > 0) {
        selectedEventType = state.calendar.eventFilter.filter(ef => ef.key == "eventType")[0].selectedValue; 
    }
    return {
        text: state.calendarText,
        lastLoadedMonth: state.calendar.endDate,
        lastMonthToLoad: state.calendarSettings.monthsOnCalendar[state.calendarSettings.monthsOnCalendar.length - 1],
        selectedEventType: selectedEventType,
        eventMonths: state.calendar.events,
        currentFilters: new Map(state.calendar.eventFilter.map(filter => [filter.key, filter.selectedValue]) as any),
        isUpdating: state.calendar.updating,
        startDate, endDate
    }
}

function mapDispatchToProps(dispatch): DispatchToProps {
    return {
        selectFilterType: (selectedValue: string) => {
            // If the selected value is null or empty string, remove that filter.
            if (!selectedValue || selectedValue === "") {
                dispatch(removeEventFilter("eventType"));
                setTimeout(() => removeLoader(), 500);
            } else {

                const filter: IEventFilter = {
                    key: "eventType",
                    selectedValue: selectedValue,
                    filter: generateEventFilter("eventType", selectedValue)
                }
                dispatch(upsertEventFilter(filter, false));
                //setTimeout(() => removeLoader(), 500);
            }
        },
        getFirstOccurenceDateForFilters: (filters, lastMonthToLoad, lastLoadedMonth) => {
            CalendarApi.getFirstOccurenceForFilters(filters, lastMonthToLoad).then(firstOccuranceDate => {

                // If a month is 
                if (firstOccuranceDate != null) {
                    const toMonth = moment(firstOccuranceDate);

                    // Only change months if it's in the future.
                   
                        dispatch(setCurrentMonth(toMonth))
                    


                    const monthElement = getMonthElement(toMonth);


                    // If the month has already loaded, apply filter and remove the loader.
                    // We need a timeout because even though the data is cached, a Promise object is created.
                 
                    var scrollInterval = setInterval(() => {

                        

                        if (monthElement) {
                            removeLoader();
                            if (scrollToMonth(toMonth)) {
                                clearInterval(scrollInterval);
                            }
                        }
                    }, 500);

                } else {
                    // If the month has already loaded, apply filter and remove the loader.
                    // We need a timeout because even though the data is cached, a Promise object is created.
                    setTimeout(() => removeLoader(), 500);
                }
            }).catch(err => {
                console.error(err);
            });
        }
    }
}

export default connect<StateToProps, DispatchToProps>(mapStateToProps, mapDispatchToProps)(CalendarEventToggleBase);